import React, { useEffect, useMemo, useRef } from 'react';
import { getReportType } from '../../../config/reportTypes';
import type { ExecutiveSummaryPreferences, SiteRagReport } from '../../../types/siteRag';
import {
  latestEligibleExecutiveSummarySources,
} from '../../../lib/buildExecutiveSummarySourceDigest';
import { validateExecutiveSummaryPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateExecutiveSummaryPreferences };

interface Props {
  value: ExecutiveSummaryPreferences;
  onChange: (next: ExecutiveSummaryPreferences) => void;
  siteReports?: SiteRagReport[];
}

function formatReportMeta(report: SiteRagReport): string {
  const date = report.createdAt;
  return date ? new Date(date).toLocaleDateString() : '';
}

function reportTypeHeading(report: SiteRagReport): string {
  const shortLabel = report.reportType ? getReportType(report.reportType).shortLabel : 'Report';
  const major = report.version ?? 1;
  const minor = report.minorVersion ?? 0;
  return `${shortLabel} · v${major}.${minor}`;
}

export const ExecutiveSummaryPreferencesForm: React.FC<Props> = ({
  value,
  onChange,
  siteReports = [],
}) => {
  const v: ExecutiveSummaryPreferences = {
    sourceReportIds: value?.sourceReportIds ?? [],
    synthesisFocus: value?.synthesisFocus ?? '',
  };

  const eligible = useMemo(
    () => latestEligibleExecutiveSummarySources(siteReports),
    [siteReports],
  );

  const eligibleIds = useMemo(() => new Set(eligible.map(r => r.id)), [eligible]);

  // #25 — Decision Guide as a LIVING doc. The Executive Summary IS the
  // Decision Guide; it should default to synthesizing EVERY completed source
  // report so the user always gets a current, all-up GO/CONDITIONAL/NO-GO
  // call without hand-picking sources. On first reach with an empty
  // selection we auto-select ALL eligible sources (the wizard seed normally
  // pre-fills this — this is the belt-and-suspenders for any entry path that
  // lands here with no sources). The auto-seed fires AT MOST ONCE per mount
  // (latched) so a deliberate "Clear" is respected and never re-filled.
  const autoSeededRef = useRef(false);
  useEffect(() => {
    const ids = value?.sourceReportIds ?? [];
    if (!autoSeededRef.current && ids.length === 0 && eligible.length > 0) {
      autoSeededRef.current = true;
      onChange({
        sourceReportIds: eligible.map(r => r.id),
        synthesisFocus: value?.synthesisFocus ?? '',
      });
    }
  }, [eligible, value?.sourceReportIds, value?.synthesisFocus, onChange]);

  // Prune any selected ids that are no longer eligible (a source report was
  // deleted / superseded since the selection was made). Independent of the
  // one-time auto-seed so it keeps the selection valid for the whole session.
  useEffect(() => {
    const ids = value?.sourceReportIds ?? [];
    const pruned = ids.filter(id => eligibleIds.has(id));
    if (pruned.length === ids.length) return;
    onChange({
      sourceReportIds: pruned,
      synthesisFocus: value?.synthesisFocus ?? '',
    });
  }, [eligibleIds, value?.sourceReportIds, value?.synthesisFocus, onChange]);

  const selected = new Set(v.sourceReportIds);

  const toggle = (reportId: string) => {
    const next = new Set(selected);
    if (next.has(reportId)) next.delete(reportId);
    else next.add(reportId);
    onChange({ ...v, sourceReportIds: [...next] });
  };

  const selectAll = () => {
    onChange({ ...v, sourceReportIds: eligible.map(r => r.id) });
  };

  const clearAll = () => {
    onChange({ ...v, sourceReportIds: [] });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-text-secondary leading-relaxed">
          Select which completed site reports to synthesize into one concise brief.
          The output focuses on <strong className="font-medium text-text-primary">behind-the-meter (BTM) energy viability</strong> for a data center at this site — grid path, cooling/water, environmental/permitting, layout, and stakeholder posture.
        </p>
      </div>

      {eligible.length === 0 ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No eligible source reports yet. Generate at least one domain report (feasibility, power, water, environmental, etc.) for this site first.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <button type="button" className="text-xs text-primary hover:underline" onClick={selectAll}>
              Select all ({eligible.length})
            </button>
            <span className="text-text-muted">·</span>
            <button type="button" className="text-xs text-text-muted hover:underline" onClick={clearAll}>
              Clear
            </button>
          </div>
          <ul className="space-y-2 max-h-64 overflow-y-auto border border-border rounded-lg p-2">
            {eligible.map((report) => (
              <li key={report.id}>
                <label className="flex items-start gap-3 cursor-pointer rounded-md px-2 py-2 hover:bg-surface-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(report.id)}
                    onChange={() => toggle(report.id)}
                    data-testid={`exec-summary-source-${report.id}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-text-primary truncate">
                      {reportTypeHeading(report)}
                    </span>
                    {formatReportMeta(report) ? (
                      <span className="block text-xs text-text-muted">{formatReportMeta(report)}</span>
                    ) : null}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">
          Synthesis focus (optional)
        </label>
        <textarea
          className="w-full min-h-[72px] text-sm border border-border rounded-lg px-3 py-2"
          placeholder="e.g. Phase 1 gas BTM for 250 MW AI training load; board-ready one-pager for IC"
          value={v.synthesisFocus ?? ''}
          onChange={(e) => onChange({ ...v, synthesisFocus: e.target.value })}
        />
      </div>
    </div>
  );
};
